import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
} from "@mui/material";
import {
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    Legend,
} from "recharts";
import { useTheme } from "@mui/material/styles";
import {
    People,
    PersonAdd,
    PhoneIphone,
    Language,
    LocalShipping,
    Receipt,
    DirectionsCar,
    CallMerge,
    DirectionsBoat,
    Inventory2Outlined,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";

import statsService from "../../services/statsService";
import PageHeader from "../../components/common/PageHeader";
import KpiCard from "../../components/common/KpiCard";
import SkeletonLoader from "../../components/common/SkeletonLoader";

const COLORS = ["#2563EB", "#0E9F6E", "#F59E0B", "#EF4444", "#8B5CF6", "#14B8A6", "#F97316", "#EC4899", "#64748B", "#22C55E"];

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Server returns sparse buckets (only days/weeks/months that actually had at
// least one row) — turned into a chart-friendly {label, count} array here,
// keyed the same way the aggregation grouped them.
const formatDaily = (series) =>
    (series || []).map((s) => ({
        label: `${MONTH_NAMES[s._id.month - 1]} ${s._id.day}`,
        count: s.count,
    }));
const formatWeekly = (series) =>
    (series || []).map((s) => ({
        label: `${s._id.year} W${s._id.week}`,
        count: s.count,
    }));
const formatMonthly = (series) =>
    (series || []).map((s) => ({
        label: `${MONTH_NAMES[s._id.month - 1]} ${s._id.year}`,
        count: s.count,
    }));

const titleCase = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

// Recharts' <Tooltip> defaults to a plain white box with black text via its
// own internal style — that's hardcoded, not a theme token, so it stayed
// bright white-on-light even in dark mode (readable, but a jarring light
// rectangle popping up over an otherwise-dark page — the "hovering the
// graphs are not correct" bug). Passing these props makes the tooltip
// itself follow the active theme like everything else on the page.
const chartTooltipProps = (theme) => ({
    contentStyle: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        fontSize: 12,
    },
    labelStyle: { color: theme.palette.text.primary, fontWeight: 600 },
    itemStyle: { color: theme.palette.text.secondary },
    cursor: { fill: theme.palette.action.hover },
});

const StatusPieCard = ({ title, breakdown, loading }) => {
    const theme = useTheme();
    const chartData = useMemo(
        () => Object.entries(breakdown || {}).map(([status, count]) => ({ name: titleCase(status), value: count })),
        [breakdown],
    );

    return (
        <Card>
            <CardHeader title={<Typography fontWeight={700} fontSize={14}>{title}</Typography>} sx={{ pb: 0 }} />
            {loading ? (
                <SkeletonLoader rows={4} />
            ) : chartData.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No data yet.
                </Typography>
            ) : (
                <CardContent>
                    <Box sx={{ width: "100%", height: 280 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={85}
                                    innerRadius={42}
                                    paddingAngle={3}
                                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip {...chartTooltipProps(theme)} />
                                <Legend verticalAlign="bottom" height={36} formatter={(value) => <span style={{ fontSize: 12 }}>{value}</span>} />
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>
                </CardContent>
            )}
        </Card>
    );
};

// Same "added over time" bar chart shown three times on this page (users,
// shipments, vehicle shipments) — each with its own Daily/Weekly/Monthly
// toggle and color, driven by the same {dailySeries, weeklySeries,
// monthlySeries} shape every getAdminOverview series already comes back as.
const TrendBarCard = ({ title, seriesHolder, period, onPeriodChange, loading, barName, barColor, emptyLabel }) => {
    const theme = useTheme();
    const chartData = useMemo(() => {
        if (!seriesHolder) return [];
        if (period === "weekly") return formatWeekly(seriesHolder.weeklySeries);
        if (period === "monthly") return formatMonthly(seriesHolder.monthlySeries);
        return formatDaily(seriesHolder.dailySeries);
    }, [seriesHolder, period]);

    return (
        <Card>
            <CardHeader
                title={<Typography fontWeight={700} fontSize={14}>{title}</Typography>}
                action={
                    <ToggleButtonGroup size="small" exclusive value={period} onChange={(_, v) => v && onPeriodChange(v)}>
                        <ToggleButton value="daily">Daily</ToggleButton>
                        <ToggleButton value="weekly">Weekly</ToggleButton>
                        <ToggleButton value="monthly">Monthly</ToggleButton>
                    </ToggleButtonGroup>
                }
                sx={{ pb: 0 }}
            />
            {loading ? (
                <SkeletonLoader rows={4} />
            ) : chartData.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {emptyLabel}
                </Typography>
            ) : (
                <CardContent>
                    <Box sx={{ width: "100%", height: 300 }}>
                        <ResponsiveContainer>
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: theme.palette.text.disabled }} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: theme.palette.text.disabled }} />
                                <Tooltip {...chartTooltipProps(theme)} />
                                <Bar dataKey="count" name={barName} fill={barColor} radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                </CardContent>
            )}
        </Card>
    );
};

const AdminStats = () => {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [registrationPeriod, setRegistrationPeriod] = useState("daily");
    const [shipmentPeriod, setShipmentPeriod] = useState("daily");
    const [vehicleShipmentPeriod, setVehicleShipmentPeriod] = useState("daily");

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data: res } = await statsService.getAdminOverview();
                setData(res.data);
            } catch {
                enqueueSnackbar("Failed to load stats", { variant: "error" });
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const users = data?.users;
    const platform = data?.platform;
    const shipments = data?.shipments;
    const vehicleShipments = data?.vehicleShipments;
    const other = data?.other;

    const platformChartData = useMemo(() => {
        if (!platform) return [];
        return [
            { name: "Web only", value: platform.webOnly },
            { name: "Mobile only", value: platform.mobileOnly },
            { name: "Both", value: platform.both },
            { name: "Never logged in", value: platform.neverLoggedIn },
        ].filter((d) => d.value > 0);
    }, [platform]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader title="Stats & Analytics" subtitle="Platform-wide usage and activity metrics" />

            {/* User KPIs */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
                <KpiCard
                    label="Total Users"
                    value={loading ? "—" : (users?.total ?? 0).toString()}
                    icon={<People />}
                    iconBg="#EBF4FF"
                    iconColor="#2B6CB0"
                />
                <KpiCard
                    label="New Today"
                    value={loading ? "—" : (users?.newToday ?? 0).toString()}
                    icon={<PersonAdd />}
                    iconBg="#ECFDF5"
                    iconColor="#0E9F6E"
                />
                <KpiCard
                    label="New This Week"
                    value={loading ? "—" : (users?.newThisWeek ?? 0).toString()}
                    icon={<PersonAdd />}
                    iconBg="#FFFBEB"
                    iconColor="#C05621"
                />
                <KpiCard
                    label="New This Month"
                    value={loading ? "—" : (users?.newThisMonth ?? 0).toString()}
                    icon={<PersonAdd />}
                    iconBg="#F5F3FF"
                    iconColor="#6D28D9"
                />
            </Box>

            {/* Registrations trend */}
            <TrendBarCard
                title="User Registrations"
                seriesHolder={users}
                period={registrationPeriod}
                onPeriodChange={setRegistrationPeriod}
                loading={loading}
                barName="New Users"
                barColor="#2563EB"
                emptyLabel="No registrations in this period."
            />

            {/* Platform usage + shipment status */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                <Card>
                    <CardHeader title={<Typography fontWeight={700} fontSize={14}>Web vs Mobile Usage</Typography>} sx={{ pb: 0 }} />
                    {loading ? (
                        <SkeletonLoader rows={4} />
                    ) : (
                        <CardContent>
                            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Language sx={{ fontSize: 18, color: "#2B6CB0" }} />
                                    <Typography variant="body2">
                                        <strong>{platform?.anyWeb ?? 0}</strong> used web
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <PhoneIphone sx={{ fontSize: 18, color: "#0E9F6E" }} />
                                    <Typography variant="body2">
                                        <strong>{platform?.anyMobile ?? 0}</strong> used mobile
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ width: "100%", height: 260 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={platformChartData}
                                            dataKey="value"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={80}
                                            innerRadius={40}
                                            paddingAngle={3}
                                            label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                                        >
                                            {platformChartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip {...chartTooltipProps(theme)} />
                                        <Legend verticalAlign="bottom" height={36} formatter={(value) => <span style={{ fontSize: 12 }}>{value}</span>} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    )}
                </Card>

                <StatusPieCard title="Shipment Status Breakdown" breakdown={shipments?.statusBreakdown} loading={loading} />
            </Box>

            {/* Shipment KPIs */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
                <KpiCard
                    label="Total Shipments"
                    value={loading ? "—" : (shipments?.total ?? 0).toString()}
                    icon={<LocalShipping />}
                    iconBg="#EBF4FF"
                    iconColor="#2B6CB0"
                />
                <KpiCard
                    label="New Today"
                    value={loading ? "—" : (shipments?.newToday ?? 0).toString()}
                    icon={<LocalShipping />}
                    iconBg="#ECFDF5"
                    iconColor="#0E9F6E"
                />
                <KpiCard
                    label="New This Week"
                    value={loading ? "—" : (shipments?.newThisWeek ?? 0).toString()}
                    icon={<LocalShipping />}
                    iconBg="#FFFBEB"
                    iconColor="#C05621"
                />
                <KpiCard
                    label="New This Month"
                    value={loading ? "—" : (shipments?.newThisMonth ?? 0).toString()}
                    icon={<LocalShipping />}
                    iconBg="#F5F3FF"
                    iconColor="#6D28D9"
                />
            </Box>

            {/* Shipments added trend */}
            <TrendBarCard
                title="Shipments Added"
                seriesHolder={shipments}
                period={shipmentPeriod}
                onPeriodChange={setShipmentPeriod}
                loading={loading}
                barName="New Shipments"
                barColor="#0E9F6E"
                emptyLabel="No shipments in this period."
            />

            {/* Vehicle shipment KPIs */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
                <KpiCard
                    label="Total Vehicle Shipments"
                    value={loading ? "—" : (vehicleShipments?.total ?? 0).toString()}
                    icon={<DirectionsCar />}
                    iconBg="#EBF4FF"
                    iconColor="#2B6CB0"
                />
            </Box>

            {/* Vehicle shipments added trend */}
            <TrendBarCard
                title="Vehicle Shipments Added"
                seriesHolder={vehicleShipments}
                period={vehicleShipmentPeriod}
                onPeriodChange={setVehicleShipmentPeriod}
                loading={loading}
                barName="New Vehicle Shipments"
                barColor="#8B5CF6"
                emptyLabel="No vehicle shipments in this period."
            />

            {/* Other platform counts */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 2 }}>
                <KpiCard
                    label="Invoices"
                    value={loading ? "—" : (other?.totalInvoices ?? 0).toString()}
                    icon={<Receipt />}
                    iconBg="#EBF4FF"
                    iconColor="#2B6CB0"
                />
                <KpiCard
                    label="Vehicle Shipments"
                    value={loading ? "—" : (other?.totalVehicleShipments ?? 0).toString()}
                    icon={<DirectionsCar />}
                    iconBg="#ECFDF5"
                    iconColor="#0E9F6E"
                />
                <KpiCard
                    label="Consolidations"
                    value={loading ? "—" : (other?.totalConsolidations ?? 0).toString()}
                    icon={<CallMerge />}
                    iconBg="#FFFBEB"
                    iconColor="#C05621"
                />
                <KpiCard
                    label="Sailing Schedules"
                    value={loading ? "—" : (other?.totalSailingSchedules ?? 0).toString()}
                    icon={<DirectionsBoat />}
                    iconBg="#F5F3FF"
                    iconColor="#6D28D9"
                />
                <KpiCard
                    label="Open Unclaimed Packages"
                    value={loading ? "—" : (other?.openUnclaimedPackages ?? 0).toString()}
                    icon={<Inventory2Outlined />}
                    iconBg="#FEF2F2"
                    iconColor="#C53030"
                />
            </Box>
        </Box>
    );
};

export default AdminStats;
